/**
 * @file Showcase 更多控件预览区块
 * @description InputNumber / TimePicker / Cascader / Transfer / Image 等预览。
 *
 * @whenToUse 仅由 `src/pages/showcase-page.tsx` 组合引用。
 * @whenNotToUse 业务页面或通用 ui 控件实现。
 */

import { useState } from 'react'
import {
  Binary,
  CalendarClock,
  Clock,
  GitBranch,
  Image as ImageIcon,
  ArrowLeftRight,
} from 'lucide-react'
import cascaderPrompt from '@/docs/components/cascader.md?raw'
import dateTimePickerPrompt from '@/docs/components/date-time-picker.md?raw'
import imagePrompt from '@/docs/components/image.md?raw'
import inputNumberPrompt from '@/docs/components/input-number.md?raw'
import timePickerPrompt from '@/docs/components/time-picker.md?raw'
import transferPrompt from '@/docs/components/transfer.md?raw'
import { Cascader } from '@/components/ui/cascader.tsx'
import { DateTimePicker } from '@/components/ui/date-time-picker.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Image } from '@/components/ui/image.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { ShowcaseCard } from '@/components/ui/showcase-card.tsx'
import { TimePicker } from '@/components/ui/time-picker.tsx'
import { Transfer } from '@/components/ui/transfer.tsx'

const regionOptions = [
  {
    value: 'zj',
    label: '浙江',
    children: [
      {
        value: 'hz',
        label: '杭州',
        children: [
          { value: 'xh', label: '西湖' },
          { value: 'yh', label: '余杭' },
        ],
      },
      {
        value: 'nb',
        label: '宁波',
        children: [{ value: 'hs', label: '海曙' }],
      },
    ],
  },
  {
    value: 'js',
    label: '江苏',
    children: [
      {
        value: 'nj',
        label: '南京',
        children: [{ value: 'gl', label: '鼓楼' }],
      },
    ],
  },
]

const transferData = [
  { key: '1', title: '用户管理' },
  { key: '2', title: '角色管理' },
  { key: '3', title: '菜单配置' },
  { key: '4', title: '操作日志' },
  { key: '5', title: '系统设置' },
  { key: '6', title: '数据导出' },
]

export function ShowcaseMoreControlsSections() {
  return (
    <>
      <ShowcaseCard
        id="input-number"
        title="InputNumber"
        description="数字步进输入。"
        icon={<Binary className="size-4" aria-hidden />}
        prompt={inputNumberPrompt}
      >
        <InputNumberDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="time-datetime"
        title="TimePicker / DateTimePicker"
        description="时分与日期时间。"
        icon={<Clock className="size-4" aria-hidden />}
        prompt={`${timePickerPrompt}\n\n---\n\n${dateTimePickerPrompt}`}
      >
        <TimeDateTimeDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="cascader"
        title="Cascader"
        description="多级级联选择。"
        icon={<GitBranch className="size-4" aria-hidden />}
        prompt={cascaderPrompt}
      >
        <CascaderDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="transfer"
        title="Transfer"
        description="左右穿梭多选。"
        icon={<ArrowLeftRight className="size-4" aria-hidden />}
        prompt={transferPrompt}
      >
        <TransferDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="image"
        title="Image"
        description="缩略图与点击预览。"
        icon={<ImageIcon className="size-4" aria-hidden />}
        prompt={imagePrompt}
      >
        <div className="flex flex-wrap gap-3">
          <Image
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop"
            alt="山景"
            className="h-24 w-36"
          />
          <Image
            src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&h=300&fit=crop"
            alt="林地"
            className="h-24 w-36"
            wrapperClassName="rounded-2xl"
          />
        </div>
      </ShowcaseCard>
    </>
  )
}

function InputNumberDemo() {
  const [qty, setQty] = useState<number | undefined>(3)
  return (
    <div className="max-w-xs">
      <FormField label="数量" htmlFor="demo-qty">
        <InputNumber
          id="demo-qty"
          value={qty}
          min={0}
          max={99}
          onChange={setQty}
        />
      </FormField>
      <p className="mt-2 text-xs text-muted">当前：{qty ?? '空'}</p>
    </div>
  )
}

function TimeDateTimeDemo() {
  const [time, setTime] = useState('09:30')
  const [dt, setDt] = useState('2026-08-07 14:00')
  return (
    <div className="grid max-w-xl gap-4 sm:grid-cols-2">
      <FormField label="时间">
        <TimePicker value={time} onChange={setTime} />
      </FormField>
      <FormField label="日期时间">
        <DateTimePicker value={dt} onChange={setDt} />
      </FormField>
      <p className="text-xs text-muted sm:col-span-2">
        <CalendarClock className="mr-1 inline size-3.5" aria-hidden />
        {time} · {dt}
      </p>
    </div>
  )
}

function CascaderDemo() {
  const [region, setRegion] = useState(['zj', 'hz', 'xh'])
  return (
    <div className="max-w-md">
      <Cascader
        options={regionOptions}
        value={region}
        onChange={(next) => setRegion(next)}
        placeholder="选择地区"
      />
      <p className="mt-2 text-xs text-muted">路径：{region.join(' / ')}</p>
    </div>
  )
}

function TransferDemo() {
  const [keys, setKeys] = useState(['1', '3'])
  return (
    <Transfer
      dataSource={transferData}
      targetKeys={keys}
      onChange={setKeys}
      titles={['可选权限', '已授权限']}
    />
  )
}
